import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import BlogService from '../services/BlogService';
import CallToActionSection from '../../core/components/Common/CallToActionSection';
import '../styles/BlogPostPage.css';
import { PrimaryButton } from '../../ui/components/buttons';
import SEO from '../../seo/SEO';
import seoConfig from '../../seo/seoConfig';

const BlogPostPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadArticle();
  }, [id]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      const response = await BlogService.getArticleById(id);
      
      if (response.success) {
        setArticle(response.data);
      } else {
        setError(response.error || 'Article not found');
      }
    } catch (err) {
      setError('Failed to load article');
      console.error('Error loading article:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="blog-post-page">
        <div className="container">
          <div className="blog-loading">
            <div className="loading-spinner"></div>
            <p>Loading article...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="blog-post-page">
        <div className="container">
          <div className="blog-error">
            <h2>Article Not Found</h2>
            <p>{error || 'The article you are looking for does not exist.'}</p>
            <div className="blog-error-actions">
              <PrimaryButton onClick={() => navigate('/blog')}>
                Back to Blog
              </PrimaryButton>
              <button onClick={() => navigate('/')} className="btn btn-secondary">
                Go Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-page">
      <SEO
        title={article.title}
        description={article.excerpt}
        canonical={`${seoConfig.siteUrl}/blog/${id}`}
        type="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: article.title,
          description: article.excerpt,
          author: { '@type': 'Person', name: article.author },
          datePublished: article.publishDate,
          mainEntityOfPage: `${seoConfig.siteUrl}/blog/${id}`,
        }}
      />
      {/* Back Navigation */}
      <section className="blog-post-nav">
        <div className="container">
          <Link to="/blog" className="blog-back-link">
            ← Back to Blog
          </Link>
        </div>
      </section>

      {/* Article Header */}
      <section className="blog-post-header">
        <div className="container">
          <div className="blog-post-meta">
            <span className="blog-post-category">{article.category}</span>
            <span className="blog-post-date">{formatDate(article.publishDate)}</span>
            <span className="blog-post-read-time">{article.readTime}</span>
          </div>
          
          <h1 className="blog-post-title">{article.title}</h1>
          
          <div className="blog-post-author-info">
            <span className="blog-post-author">By {article.author}</span>
          </div>
        </div>
      </section>

      {/* Article Image */}
      <section className="blog-post-image-section">
        <div className="container">
          <div className="blog-post-image">
            <ImagePlaceholder 
              width="100%"
              height="400px"
              className="blog-main-image-placeholder"
              text="Article Image"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="blog-post-content">
        <div className="container">
          <div className="blog-post-body">
            <p className="blog-post-excerpt">{article.excerpt}</p>
            
            <div className="blog-post-text">
              {article.content.split('\n').map((paragraph, index) => (
                <p key={index} className="blog-paragraph">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Tags */}
            <div className="blog-post-tags-section">
              <h4>Tags:</h4>
              <div className="blog-post-tags">
                {article.tags.map((tag, index) => (
                  <span key={index} className="blog-post-tag">#{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <CallToActionSection 
        title="Ready to Create Impactful Content?"
        subtitle="Get expert guidance and personalized solutions for your video production projects"
        buttonText="Start Your Project"
      />
    </div>
  );
};

// Simple ImagePlaceholder component for the blog post page
const ImagePlaceholder = ({ width, height, className, text }) => (
  <div 
    className={`image-placeholder ${className}`}
    style={{ width, height }}
  >
    <div className="image-placeholder__content">
      <div className="image-placeholder__svg">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <circle cx="8.5" cy="8.5" r="1.5"/>
          <polyline points="21,15 16,10 5,21"/>
        </svg>
      </div>
      <div className="image-placeholder__text">{text}</div>
    </div>
  </div>
);

export default BlogPostPage;
